import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import * as Common from '_common';
import * as Components from '_components';
import * as Utils from '_utils';
import * as Styles from './Frame.module.scss';
import { FrameSegments, FrameSegment } from './Segments';
import * as Types from './Types';
const FrameContext = React.createContext(undefined);
export function Frame(props) {
    const [sides, setSides] = React.useState({
        left: false,
        right: false,
        top: false,
        bottom: false,
    });
    const registerSide = React.useCallback((side, ctor) => {
        setSides(prev => {
            switch (side) {
                case Types.FrameComponentType.Left:
                    return { ...prev, left: ctor };
                case Types.FrameComponentType.Right:
                    return { ...prev, right: ctor };
                case Types.FrameComponentType.Top:
                    return { ...prev, top: ctor };
                case Types.FrameComponentType.Bottom:
                    return { ...prev, bottom: ctor };
                default:
                    return prev;
            }
        });
    }, []);
    const horizontalOnly = sides.top && sides.bottom && !sides.left && !sides.right;
    const verticalOnly = sides.left && sides.right && !sides.top && !sides.bottom;
    let classNames = {
        [Styles.horizontal_only]: horizontalOnly,
        [Styles.vertical_only]: verticalOnly,
    };
    classNames = Common.WithCommonProps(props, classNames);
    classNames = Components.WithTileProps(props, classNames);
    const classes = Utils.classNames(classNames);
    function getCornerType(hSide, vSide) {
        if (horizontalOnly) {
            return Styles.corner_cup;
        }
        if (verticalOnly) {
            return Styles.corner_small;
        }
        if (hSide && vSide) {
            return Styles.corner_rounded;
        }
        if (hSide) {
            return Styles.corner_simple_h;
        }
        if (vSide) {
            return Styles.corner_simple_v;
        }
        return Styles.corner_hidden;
    }
    return (_jsxs(Common.Component, { name: "frame", className: classes, ...Common.HtmlProps(props), children: [_jsx("div", { className: `${Styles.corner_top_left} ${getCornerType(sides.top, sides.left)}` }), _jsx("div", { className: `${Styles.corner_top_right} ${getCornerType(sides.top, sides.right)}` }), _jsx("div", { className: `${Styles.corner_bottom_left} ${getCornerType(sides.bottom, sides.left)}` }), _jsx("div", { className: `${Styles.corner_bottom_right} ${getCornerType(sides.bottom, sides.right)}` }), _jsx(FrameContext.Provider, { value: { registerComponent: registerSide }, children: props.children })] }));
}
export function FrameComponent(props) {
    const context = React.useContext(FrameContext);
    React.useEffect(() => {
        const isActive = props.type !== Types.FrameSideType.None;
        if (context) {
            context.registerComponent(props.side, isActive);
        }
        return () => {
            if (context) {
                context.registerComponent(props.side, false);
            }
        };
    }, [props.side, props.type]);
    const { type, side, children, isDecorated, isSegmented, ...rest } = props;
    if (type === Types.FrameSideType.None) {
        return _jsx(_Fragment, {});
    }
    // check if children contains any segments
    const hasSegments = Utils.ContainsChildrenOfType(children, FrameSegment);
    const horizontal = side === Types.FrameComponentType.Top || side === Types.FrameComponentType.Bottom;
    const vertical = side === Types.FrameComponentType.Left || side === Types.FrameComponentType.Right;
    const classes = Utils.classNames(Common.WithCommonProps(props, {
        ['context-panel']: side !== Types.FrameComponentType.Content,
        [Styles.variant_small]: type === Types.FrameSideType.Small,
        [Styles.variant_normal]: type === Types.FrameSideType.Big,
        [Styles.side_left]: side === Types.FrameComponentType.Left,
        [Styles.side_right]: side === Types.FrameComponentType.Right,
        [Styles.side_top]: side === Types.FrameComponentType.Top,
        [Styles.side_bottom]: side === Types.FrameComponentType.Bottom,
        is_decorated: isDecorated || false,
        is_segmented: props.isSegmented || isSegmented || false,
        [Styles.content]: side === Types.FrameComponentType.Content,
        horizontal: horizontal,
        vertical: vertical,
    }));
    let content = props.children;
    if (props.side === Types.FrameComponentType.Content) {
        content = _jsx("div", { className: Styles.inner, children: content });
    }
    const decoration = props.isDecorated ? _jsx("div", { className: Styles.decoration }) : _jsx(_Fragment, {});
    if (props.side === Types.FrameComponentType.Content) {
        return (_jsxs(Common.Component, { name: "frame-content", className: classes, ...rest, children: [content, decoration] }));
    }
    else {
        if (isSegmented || hasSegments) {
            return (_jsx(Common.Component, { name: "frame-side", className: classes, ...rest, children: _jsx(FrameSegments, { side: props.side, children: content }) }));
        }
        else {
            return (_jsxs(Common.Component, { name: "frame-side", className: classes, ...rest, children: [content, decoration] }));
        }
    }
}
